import { FC, useContext, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import styled from '@mui/material/styles/styled';
import { Autocomplete } from '@mui/material';
import styles from './index.module.scss';
import { listServicePoints, ServicePointsData } from 'api/query';
import { GlobalContext } from '../../storage';

// PG-188 非全家歸還點手動選擇:相機掃不到「歸還點 QR」時的逃生口。
// 用 GPS 列出最近的歸還點(全通路,排除全家 — 全家有自己的流程),選一個即設定 channel/store,
// 走跟掃到歸還點 QR 相同的後續(step 0 → step 1 掃/輸入包裝)。

const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  margin: 0,
  marginTop: 10,
  padding: theme.spacing(1),
  textAlign: 'center',
  marginBottom: 20,
}));

const DialogContent = styled(MuiDialogContent)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  minWidth: 285,
  padding: '20px',
  paddingBottom: 28,
}));

const DialogActions = styled(MuiDialogActions)(() => ({
  padding: '0 20px',
  paddingBottom: 18,
  display: 'flex',
  flexDirection: 'column',
  '& > :not(:first-of-type)': {
    marginLeft: 0,
    marginTop: 10,
  },
}));

// haversine 公里距離
const distanceKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

interface ReturnPointPickerProps {
  visible: boolean;
  onConfirm: (data?: ServicePointsData) => void;
  onClose?: () => void;
}

const ReturnPointPicker: FC<ReturnPointPickerProps> = ({ visible, onConfirm, onClose }) => {
  const { returnStorage, nearByStorage } = useContext(GlobalContext);
  const { setStoreId, setChsShortCode } = returnStorage as any;
  const { fami_me_latitude, fami_me_longitude } = nearByStorage as any;

  const [points, setPoints] = useState<ServicePointsData[]>([]);
  const [selected, setSelected] = useState<ServicePointsData | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string>('');

  // 開啟即抓服務點清單(公開 API,免登入)。相機失敗前 return.tsx 已呼叫 getLocation() 拿定位。
  useEffect(() => {
    if (!visible) {
      setSelected(undefined);
      setErrMsg('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await listServicePoints(1);
        if (cancelled) return;
        // 排除全家(全家有專屬流程),只留有座標、可用的點
        const usable = (data || []).filter(
          (p) => p.chs_short_code !== 'fami' && p.latitude && p.longitude && p.enable === 1
        );
        setPoints(usable);
        if (!usable.length) setErrMsg('目前找不到可用的歸還點,請洽客服。');
      } catch (e) {
        if (!cancelled) setErrMsg('歸還點載入失敗,請稍後再試或洽客服。');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  // 有定位就依距離排序(最近在前);沒定位就原順序。
  const sortedOptions = useMemo(() => {
    const hasGps = typeof fami_me_latitude === 'number' && typeof fami_me_longitude === 'number';
    if (!hasGps) return points;
    return [...points]
      .map((p) => ({ ...p, distance: distanceKm(fami_me_latitude, fami_me_longitude, p.latitude, p.longitude) }))
      .sort((a, b) => a.distance - b.distance);
  }, [points, fami_me_latitude, fami_me_longitude]);

  const onSelected = (_e: unknown, value: ServicePointsData | null) => {
    if (!value) return;
    setSelected(value);
    setChsShortCode(value.chs_short_code);
    setStoreId(value.store_id);
  };

  const optionLabel = (o: ServicePointsData) => {
    const dist =
      typeof o.distance === 'number' && o.distance > 0 ? `(約 ${o.distance.toFixed(1)} km)` : '';
    return `${o.channel_name ? `【${o.channel_name}】` : ''}${o.name} ${dist}`.trim();
  };

  return (
    <Dialog open={visible} onClose={onClose}>
      <DialogTitle>請選擇歸還點</DialogTitle>
      <DialogContent>
        <div className={styles.manualContainer}>
          <Autocomplete
            sx={{
              display: 'flex',
              flex: 1,
              width: '100%',
              alignItems: 'center',
              '& > div': { width: '100%' },
              '& input': {
                width: '100%',
                outline: 'none',
                fontSize: 16,
                border: '1px solid #00000029',
                height: 58,
                padding: '0 10px',
              },
            }}
            options={sortedOptions}
            noOptionsText={loading ? '載入中...' : '無歸還點'}
            loading={loading}
            loadingText="載入中..."
            onChange={onSelected}
            isOptionEqualToValue={(o, v) => o.store_id === v.store_id}
            getOptionLabel={optionLabel}
            renderInput={(params) => (
              <div ref={params.InputProps.ref}>
                <input type="text" placeholder="請下拉選取最近的歸還點" {...params.inputProps} />
              </div>
            )}
          />
          {selected && (
            <div className={styles.selectedInfo}>
              <span>歸還點:{optionLabel(selected)}</span>
            </div>
          )}
          {!selected && errMsg !== '' && (
            <div className={styles.selectedInfo}>
              <span style={{ color: 'red' }}>{errMsg}</span>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        {selected && (
          <button className={`${styles.actionButton}`} onClick={() => onConfirm(selected)}>
            確定
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReturnPointPicker;
